/**
 * Trigger a file download for a URL without leaving the user on a blank tab.
 *
 * The naive approach — `window.open(url, '_blank')` — opens a new tab and then
 * relies on the server's `Content-Disposition: attachment` to start the
 * download. The download fires, but the freshly-opened tab is never navigated
 * anywhere useful, so the user is stranded staring at `about:blank`.
 *
 * A programmatic anchor click has none of that baggage: when the server
 * responds with an attachment disposition (as every report/export endpoint
 * does), the browser downloads the file in place and never opens a tab or
 * navigates the current page away from the app. Because it's a genuine
 * download rather than a popup, it is not subject to popup blockers, so it
 * works even after an intervening `await` (e.g. fetching a presigned token).
 *
 * For same-origin URLs the optional `filename` sets the saved name; for
 * cross-origin download endpoints the browser ignores `download` and uses the
 * server-provided filename, which is the desired behaviour there.
 */
export function downloadUrl(url: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = url;
  // An empty `download` attribute asks the browser to download rather than
  // navigate (honoured for same-origin; harmless cross-origin).
  link.download = filename ?? '';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
